import React, { useMemo } from 'react'
import { graphql, useLazyLoadQuery } from 'react-relay'
import { useParams } from 'react-router-dom'

import { YStack } from 'tamagui'

import { getSlugFromPrettyUrl } from '@data/util/urlHelpers'

import { EventMetadata } from '@components/Event/EventMetadata'
import { Card } from '@newComponents/Card'
import { GradientBackgroundScreen } from '@newComponents/Screen'
import { H2, Text } from '@newComponents/Text'

import { EventItem } from '@newComponents/Event/EventItem'

import { EventAttendContent } from './EventAttendContent'

import { EventAttendScreenQuery } from './__generated__/EventAttendScreenQuery.graphql'

export const EventAttendScreen = () => {
  const params = useParams<'eventId'>()

  const data = useLazyLoadQuery<EventAttendScreenQuery>(
    graphql`
      query EventAttendScreenQuery($slug: String!) {
        event: eventBySlug(slug: $slug) {
          name
          eventTicketingTiers {
            totalCount
          }

          owner {
            __typename

            ... on User {
              firstName
              lastName
            }

            ... on Guild {
              name

              myMembership {
                membershipLevel
              }
            }
          }

          myAttendance {
            ticketPurchase {
              status
            }
          }

          ...EventItemNew_event
          ...EventAttendContent_event
          ...EventMetadata_event
        }

        ...EventItemNew_query
        ...EventAttendContent_query
      }
    `,
    { slug: getSlugFromPrettyUrl(params.eventId) }
  )

  const name = useMemo(() => {
    if (!data.event) {
      return ''
    }

    switch (data.event.owner?.__typename) {
      case 'Guild': {
        return data.event.owner.name
      }

      case 'User': {
        return `${data.event.owner.firstName} ${data.event.owner.lastName}`
      }
    }

    return ''
  }, [data])

  if (!data.event) {
    return null
  }

  const pageTitle =
    data.event.eventTicketingTiers.totalCount > 0 &&
    data.event.myAttendance?.ticketPurchase?.status !== 'SUCCEEDED'
      ? `Purchase Ticket for ${data.event.name}`
      : `Confirm Attendance for ${data.event.name}`

  return (
    <>
      <EventMetadata title={pageTitle} event={data.event} />

      <GradientBackgroundScreen alignItems='center' gap='$8'>
        <EventItem
          event={data.event}
          query={data}
          maxWidth={600}
          width='100%'
          showAttendButton={false}
          displayFullDate
          displayVisibility={false}
        />

        <Card gap='$6' maxWidth={600} width='100%'>
          <YStack gap='$1'>
            <H2>Almost there...</H2>

            <Text size='$4'>
              {data.event.eventTicketingTiers.totalCount > 0 &&
              data.event.myAttendance?.ticketPurchase?.status !== 'SUCCEEDED'
                ? `Do you want to purchase a ticket for ${data.event.name}?`
                : data.event.owner?.__typename === 'Guild' &&
                  data.event.owner.myMembership?.membershipLevel
                ? `We're confirming your attendance for ${data.event.name}`
                : `We need your consent to share information with ${name}`}
            </Text>
          </YStack>

          <EventAttendContent event={data.event} query={data} />
        </Card>
      </GradientBackgroundScreen>
    </>
  )
}

export default EventAttendScreen
